import { UserEvent, CCMail, Session, Promo, UserType } from '@ccmn/rhino';
import { batch, useSignalEffect } from '@preact/signals';
import { useStore } from 'src/hooks';
import { ROUTE, ROUTE_STUDIO, WORKER } from 'src/common/constants';
import { redirectURL } from 'src/common/utils';

const STORAGE_KEY = 'ccmn.clientID';

interface PortMessageData {
    cmd: WORKER.COMMAND;
    token: string;
    client?: number;
    wsUrl?: string;
    bcName?: string;
}

const WSStatus = () => {
    const ctx = useStore();
    const userID = ctx.user.userID.value;
    const STATUS_BACKEND = ctx.config.statusBackend.value;
    const clientID: string = localStorage.getItem(`${STORAGE_KEY}.${userID}`) ?? Math.floor(Math.random() * 1000).toString();

    if (clientID) {
        localStorage.setItem(`${STORAGE_KEY}.${userID}`, clientID);
    }

    const cookie: RegExpMatchArray | null = document.cookie.match(/_QA=([a-z0-9]*)/i);
    const QS = cookie ? `?${cookie[0]}` : '';

    const WEBSOCKET_PROTOCOL = window.location.protocol === 'https:' ? 'wss' : 'ws'; // Define WEBSOCKET_PROTOCOL based on the current protocol
    const WEBSOCKET_URL = `${WEBSOCKET_PROTOCOL}://${STATUS_BACKEND}/rhino/status/${clientID}${QS}`;
    let worker: SharedWorker;
    let broadcastChannel: BroadcastChannel;
    let isWorkerStarted = false;

    const handleWorkerStart = () => {
        if (!STATUS_BACKEND) {
            return;
        }

        if (isWorkerStarted) {
            return;
        }

        try {
            // @ts-ignore
            worker = new SharedWorker(new URL('src/worker.ts', import.meta.url) /* webpackChunkName: 'shared-worker.js' */, {
                credentials: 'include',
                name: WORKER.SHARED_WORKER_NAME,
            });

            worker.port.start();

            broadcastChannel = new BroadcastChannel(`${WORKER.BROADCAST_CHANNEL_NAME}.${userID}`);
            broadcastChannel.addEventListener('message', (message: MessageEvent) => {
                if (message.data.type === WORKER.TYPE.MESSAGE) {
                    return handleBroadcast(message.data);
                }

                if (message.data.type === WORKER.TYPE.LOGOUT) {
                    return handleLogout();
                }
            });

            isWorkerStarted = true;
        } catch {
            console.warn('SharedWorker and/or BroadcastChannel are not supported');
        }
    };

    const handlePortMessage = (message: PortMessageData) => {
        if (worker) {
            return worker.port.postMessage(message);
        }
    };

    const handleBroadcast = (message: MessageEvent) => {
        handleEvent(message.data);
    };

    const handleWorkerClose = () => {
        if (!isWorkerStarted) {
            return;
        }

        if (worker) {
            worker.port.postMessage({
                cmd: WORKER.COMMAND.DISCONNECT,
                client: userID,
            });
        }

        if (broadcastChannel) {
            broadcastChannel.close();
        }

        isWorkerStarted = false;
    };

    useSignalEffect(() => {
        if (ctx.user.token) {
            const token = ctx.user.token.value;

            if (isWorkerStarted && token) {
                return handlePortMessage({
                    cmd: WORKER.COMMAND.UPDATE,
                    client: userID,
                    token,
                });
            }

            if (!isWorkerStarted && token) {
                handleWorkerStart();
                setTimeout(() => {
                    return handlePortMessage({
                        cmd: WORKER.COMMAND.CONNECT,
                        client: userID,
                        token,
                        wsUrl: WEBSOCKET_URL,
                        bcName: `${WORKER.BROADCAST_CHANNEL_NAME}.${userID}`,
                    });
                }, 500);
            }
        } else {
            handleWorkerClose();
        }
    });

    const handleLogout = () => {
        const homepageURL = ctx.user.userTypeID.value === UserType.CHATHOST ? ROUTE.LOGON : ROUTE_STUDIO.LOGON;
        batch(() => {
            ctx.user.ccMailNewMessagesCount.value = undefined;
            ctx.user.isLoggedIn.value = undefined;
            ctx.user.isStudioMember.value = undefined;
            ctx.user.profileUrl.value = undefined;
            ctx.user.screenName.value = undefined;
            ctx.user.showRegWizard.value = undefined;
            ctx.user.token.value = null;
        });
        redirectURL(homepageURL, true);
    };

    const handleEvent = (userEvent: UserEvent) => {
        switch (userEvent.event) {
            case CCMail.event.CC_MAIL:
                ctx.user.ccMailNewMessagesCount.value = userEvent.unread_count;
                break;
            case Session.event.SESSION:
                batch(() => {
                    ctx.user.ccMailNewMessagesCount.value = userEvent.ccMailNewMessagesCount;
                    ctx.user.isLoggedIn.value = userEvent.isLoggedIn;
                    ctx.user.isStudioMember.value = userEvent.isStudioMember;
                    ctx.user.profileUrl.value = userEvent.profileURL;
                    ctx.user.screenName.value = userEvent.screenName;
                    ctx.user.showRegWizard.value = userEvent.showRegWizard;
                    ctx.user.token.value = userEvent.token ?? '';
                    ctx.user.userTypeID.value = userEvent.userTypeID;
                });
                break;
            case Promo.event.PROMO:
                batch(() => {
                    ctx.promo.type.value = userEvent.type;
                    ctx.promo.countdownStart.value = userEvent.countdownStart;
                    ctx.promo.countdownEnd.value = userEvent.countdownEnd;
                    ctx.promo.phaseCounter.value = userEvent.phaseCounter;
                    ctx.promo.phaseEvent.value = userEvent.phaseEvent;
                    ctx.promo.phaseGame.value = userEvent.phaseGame;
                    ctx.promo.phaseHistory.value = userEvent.phaseHistory;
                });
                break;
            default:
                console.log('Got unknown userEvent', userEvent);
        }
    };

    return null;
};

export default WSStatus;
